import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { extensionDecorator, ExtensionPriority, MarkExtension, Cast, markPasteRule, getMatchString, LEAF_NODE_REPLACING_CHARACTER, findMatches, markEqualsType } from '@remirror/core';
import { TextSelection } from '@remirror/pm/state';
import { ReplaceStep } from '@remirror/pm/transform';

var _dec, _class;

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
/**
 * An auto complete auto decorated linker. This is more aggressive than the
 * `@remirror/extension-link` in that it wraps any url like string as a mark.
 *
 * It's inspired by the behavior of social sites like `twitter`.
 */

var AutoLinkExtension = (_dec = /*#__PURE__*/extensionDecorator({
  defaultOptions: {
    urlRegex: /((http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[\da-z]+([.-][\da-z]+)*\.[a-z]{2,5}(:\d{1,5})?(\/.*)?)/gi,
    defaultProtocol: ''
  },
  defaultPriority: ExtensionPriority.Low,
  handlerKeys: ['onUrlUpdate'],
  staticKeys: ['urlRegex']
}), /*#__PURE__*/_dec(_class = class AutoLinkExtension extends MarkExtension {
  get name() {
    return 'autoLink';
  }

  createMarkSpec(extra) {
    return {
      attrs: _objectSpread(_objectSpread({}, extra.defaults()), {}, {
        href: {
          default: null
        }
      }),
      inclusive: false,
      spanning: false,
      parseDOM: [{
        tag: 'a[href]',
        getAttrs: node => _objectSpread(_objectSpread({}, extra.parse(node)), {}, {
          href: Cast(node).getAttribute('href')
        })
      }],
      toDOM: node => {
        return ['a', _objectSpread(_objectSpread(_objectSpread({}, extra.dom(node)), node.attrs), {}, {
          role: 'presentation'
        }), 0];
      }
    };
  }

  createPasteRules() {
    return [markPasteRule({
      regexp: this.options.urlRegex,
      type: this.type,
      getAttributes: url => {
        return {
          href: extractHref(getMatchString(url), this.options.defaultProtocol)
        };
      }
    })];
  }

  createPlugin() {
    return {
      state: {
        init: () => {
          return null;
        },
        apply: (tr, previous) => {
          var stored = tr.getMeta(this.pluginKey);
          return stored ? stored : tr.selectionSet || tr.docChanged ? null : previous;
        }
      },
      // Runs through the current line (and previous line if it exists) to reapply
      // social link marks to the relevant parts of text.
      // TODO extract this as a standalone prosemirror plugin
      appendTransaction: (transactions, _oldState, state) => {
        // Used to represent leaf nodes as text otherwise they just get replaced
        var leafChar = ' ';
        var selection = state.selection,
            doc = state.doc;
        var $from = selection.$from,
            $to = selection.$to,
            from = selection.from,
            to = selection.to;
        var hasReplaceTransactions = transactions.some((_ref) => {
          var steps = _ref.steps;
          return steps.some(step => step instanceof ReplaceStep);
        });

        if (!hasReplaceTransactions) {
          return;
        } // Check that the mark should still be active


        var searchText = doc.textBetween($from.start(), from, LEAF_NODE_REPLACING_CHARACTER, leafChar) + doc.textBetween(to, $to.end(), LEAF_NODE_REPLACING_CHARACTER, leafChar);
        var tr = state.tr;
        var collectedParameters = []; // If at the start of a new line (i.e. new block added and not at the start of the document)

        if (from === $from.start() && from >= 2) {
          var $pos = doc.resolve(from - 2);
          var previousSearchText = doc.textBetween($pos.start(), $pos.end(), LEAF_NODE_REPLACING_CHARACTER, leafChar);
          findMatches(previousSearchText, this.options.urlRegex).forEach(match => {
            var startIndex = match.index;
            var url = match[1];
            var start = $pos.start() + startIndex;
            var end = $pos.start() + startIndex + match[0].length;
            collectedParameters.push({
              state,
              url,
              from: start,
              to: end,
              type: this.type,
              defaultProtocol: this.options.defaultProtocol
            });
          });
          tr.removeMark($pos.start(), $pos.end(), this.type);
        } // Finds matches within the current node when in the middle of a node


        findMatches(searchText, this.options.urlRegex).forEach(match => {
          var startIndex = match.index;
          var url = match[1];
          var start = $from.start() + startIndex;
          var end = $from.start() + startIndex + match[0].length; // The text directly before the match

          var textBefore = doc.textBetween(start - 1, start, LEAF_NODE_REPLACING_CHARACTER, leafChar);

          if (!/\w/.test(textBefore)) {
            collectedParameters.push({
              state,
              url,
              from: start,
              to: end,
              type: this.type,
              defaultProtocol: this.options.defaultProtocol
            });
          }
        }); // Remove all marks within the current block

        tr.removeMark($from.start(), $from.end(), this.type); // Add all marks again for the nodes

        for (var _i = 0, _collectedParameters = collectedParameters; _i < _collectedParameters.length; _i++) {
          var collected = _collectedParameters[_i];
          autoLinkHandler(_objectSpread(_objectSpread({}, collected), {}, {
            tr
          }));
        }

        return tr;
      },
      view: () => ({
        update: (view, previousState) => {
          var next = getUrlsFromState(view.state, this.type);
          var previous = getUrlsFromState(previousState, this.type);

          if (!areSetsEqual(next.set, previous.set)) {
            this.options.onUrlUpdate(next);
          }
        }
      })
    };
  }

}) || _class);

function extractHref(url, defaultProtocol) {
  return url.startsWith('http') || url.startsWith('//') ? url : "".concat(defaultProtocol, "//").concat(url);
}

/**
 * Add the provided URL as a mark to the text range provided
 */
function autoLinkHandler(parameter) {
  var _parameter$tr;

  var state = parameter.state,
      url = parameter.url,
      from = parameter.from,
      to = parameter.to,
      type = parameter.type,
      defaultProtocol = parameter.defaultProtocol;
  var endPosition = state.selection.to;
  var autoLink = type.create({
    href: extractHref(url, defaultProtocol)
  });
  var tr = ((_parameter$tr = parameter.tr) !== null && _parameter$tr !== void 0 ? _parameter$tr : state.tr).replaceWith(from, to, state.schema.text(url, [autoLink])); // Ensure that the selection doesn't jump when the the current selection is within the range

  if (endPosition < to) {
    return tr.setSelection(TextSelection.create(tr.doc, endPosition));
  }

  return tr;
}
/**
 * Retrieves all the automatically applied URLs from the state.
 */


function getUrlsFromState(state, type) {
  var $pos = state.doc.resolve(0);
  var marks = [];
  state.doc.nodesBetween($pos.start(), $pos.end(), node => {
    marks.push(...node.marks);
  });
  var urls = [];

  for (var _i2 = 0, _marks = marks; _i2 < _marks.length; _i2++) {
    var mark = _marks[_i2];

    if (markEqualsType({
      mark,
      types: type
    })) {
      urls.push(mark.attrs.href);
    }
  }

  return {
    set: new Set(urls),
    urls
  };
}
/**
 * Checks whether two sets are equal.
 */


function areSetsEqual(setOne, setTwo) {
  if (setOne.size !== setTwo.size) {
    return false;
  }

  var _iterator = _createForOfIteratorHelper(setOne),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var value = _step.value;

      if (!setTwo.has(value)) {
        return false;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return true;
}

export { AutoLinkExtension };
